import type {
  PlotMaterialIdV1,
  ProjectDocumentV1,
  ProjectNodeV1,
  ProjectProductV1,
  TextNodeV1,
} from '@dg-embalajes/contracts';

export type EditorInteractionMode = 'table' | 'element';
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
export type ExportStatus = 'idle' | 'preparing' | 'ready' | 'error' | 'cancelled';
export type AssetRole = 'logo' | 'artwork' | 'pdf-art' | 'sticker-image' | 'background';

export interface CameraSnapshot {
  readonly zoomPercent: number;
  readonly rotationDeg: number;
  readonly orientationDeg: 0 | 90;
  readonly showDimensions: boolean;
}

export interface LayerSnapshot {
  readonly id: string;
  readonly label: string;
  readonly kind: 'background' | 'text' | 'image' | 'pdf';
  readonly selected: boolean;
  readonly visible: boolean;
  readonly locked: boolean;
  readonly canDelete: boolean;
  readonly canDuplicate: boolean;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
  readonly canMoveToFront: boolean;
  readonly canMoveToBack: boolean;
  readonly canToggleVisibility: boolean;
  readonly canLock: boolean;
  readonly canReplace: boolean;
}

export interface AssetInspection {
  readonly file: File;
  readonly kind: 'image' | 'pdf';
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly pageCount: number;
  readonly quality: 'good' | 'warning' | 'unknown';
  readonly qualityMessage: string;
}

export interface EditorNotice {
  readonly kind: 'info' | 'success' | 'warning' | 'error';
  readonly message: string;
  readonly persistent?: boolean;
}

export interface AssetQualitySnapshot {
  readonly key: string;
  readonly nodeId: string | null;
  readonly fileName: string;
  readonly role: string;
  readonly pageNumber: number | null;
  readonly status: 'good' | 'warning' | 'error';
  readonly ppi: number | null;
  readonly message: string;
}

export interface EditorSnapshot {
  readonly document: ProjectDocumentV1 | null;
  readonly recoveryDraft: { readonly name: string; readonly updatedAt: string } | null;
  readonly projectName: string;
  readonly interactionMode: EditorInteractionMode;
  readonly selectedId: string | null;
  readonly selectedNode: ProjectNodeV1 | null;
  readonly layers: readonly LayerSnapshot[];
  readonly camera: CameraSnapshot;
  readonly saveStatus: SaveStatus;
  readonly saveError: string | null;
  readonly exportStatus: ExportStatus;
  readonly exportError: string | null;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly isDirty: boolean;
  readonly valueActionCount: number;
  readonly qualityReady: boolean;
  readonly qualityMessage: string;
  readonly assetQuality: readonly AssetQualitySnapshot[];
  readonly cleanPreviewUrl: string | null;
  readonly cleanPreviewStatus: 'idle' | 'preparing' | 'ready' | 'error';
  readonly cleanPreviewError: string | null;
  readonly notice: EditorNotice | null;
}

export interface EditorSurfaceCallbacks {
  readonly onSelect: (id: string | null) => void;
  readonly onRequestTextEdit: (id: string) => void;
  readonly onViewportGestureStart?: () => void;
}

export interface TextPatch {
  readonly text?: string;
  readonly fontFamily?: string;
  readonly fontSizeMm?: number;
  readonly fillColor?: string;
  readonly strokeColor?: string | null;
  readonly strokeWidthMm?: number;
  readonly rotationDeg?: number;
}

export interface ElementTransformPatch {
  readonly scale?: number;
  readonly rotationDeg?: number;
}

export interface EditorPort {
  getSnapshot(): EditorSnapshot;
  subscribe(listener: () => void): () => void;
  mountSurface(container: HTMLElement, callbacks: EditorSurfaceCallbacks): () => void;

  createProject(product: ProjectProductV1): Promise<void>;
  replaceProduct(product: ProjectProductV1): Promise<void>;
  continueRecoveredDraft(): void;
  startNewFromRecovery(): Promise<void>;
  closeProject(): Promise<void>;
  renameProject(name: string): void;
  setInteractionMode(mode: EditorInteractionMode): void;
  select(id: string | null): void;

  undo(): void;
  redo(): void;
  fitCamera(): void;
  centerCamera(): void;
  resetCamera(): void;
  setCameraZoom(percent: number): void;
  setCameraOrientation(degrees: 0 | 90): void;
  rotateCameraBy(degrees: number): void;
  setDimensionsVisible(visible: boolean): void;

  applyCatalogBackground(patternId: string): Promise<void>;
  removeBackground(): void;
  inspectAsset(file: File, role: AssetRole): Promise<AssetInspection>;
  addAsset(inspection: AssetInspection, role: AssetRole, page: number): Promise<void>;
  fitCustomBackgroundWidth(): void;
  replaceSelectedAsset(inspection: AssetInspection, page: number): Promise<void>;

  addText(text: string): Promise<string>;
  updateText(id: string, patch: TextPatch): void;
  updateElementTransform(id: string, patch: ElementTransformPatch): void;
  centerImage(id: string): void;
  fitImage(id: string): void;
  resetImage(id: string): void;
  beginTextEditing(id: string, viewportHeight: number): void;
  endTextEditing(id: string): Promise<void>;

  duplicate(id: string): void;
  copy(id: string): void;
  cut(id: string): void;
  paste(): void;
  setVisible(id: string, visible: boolean): void;
  setLocked(id: string, locked: boolean): void;
  moveLayer(id: string, direction: 'up' | 'down' | 'front' | 'back'): void;
  delete(id: string): void;

  changeTubeMaterial(materialId: PlotMaterialIdV1): void;
  exportPdf(): Promise<Blob>;
  sharePdf(): Promise<'shared' | 'downloaded-fallback' | 'cancelled'>;
  downloadPdf(): Promise<void>;
  dismissNotice(): void;
  getDiagnosticSnapshot?(): {
    readonly document: ProjectDocumentV1 | null;
    readonly selectedId: string | null;
    readonly interactionMode: EditorInteractionMode;
    readonly gestureOwner: { readonly kind: 'CAMERA' } | { readonly kind: 'OBJECT'; readonly targetId: string } | null;
    readonly pointerRouting: {
      readonly cornerTransformActive: boolean;
      readonly directManipulationEnabled: boolean;
      readonly directPointerId: number | null;
      readonly suppressedPointerCount: number;
      readonly pointerDownEventCount: number;
      readonly lastPointerDownRoute: 'blocked' | 'direct' | 'coordinator' | 'unhandled' | null;
    };
    readonly editingTextId: string | null;
    readonly camera: CameraSnapshot;
    readonly cameraRuntime: {
      readonly panXPx: number;
      readonly panYPx: number;
      readonly zoom: number;
      readonly rotationDeg: number;
      readonly viewportWidthPx: number;
      readonly viewportHeightPx: number;
      readonly documentWidthMm: number;
      readonly documentHeightMm: number;
    } | null;
    readonly selectedScreenPointPx: { readonly x: number; readonly y: number } | null;
    readonly exportStatus: ExportStatus;
    readonly exportError: string | null;
    readonly cleanPreviewStatus: EditorSnapshot['cleanPreviewStatus'];
    readonly cleanPreviewError: string | null;
  };
}

export function isTextNode(node: ProjectNodeV1 | null): node is TextNodeV1 {
  return node?.type === 'text';
}
